/**
 * Transpose Additional Data top-table payload into Bid | Ask entity columns
 * with metric rows. Team Run sends bid_* then ask_* greek columns (very wide);
 * Audit Trail sends only one side.
 */

const BID_PREFIX = "bid";
const ASK_PREFIX = "ask";

function columnTitle(col) {
  return String(col?.title ?? "");
}

function columnKey(col) {
  return String(col?.key ?? "");
}

export function isTenorExpiryColumnTitle(title) {
  const t = String(title ?? "").toLowerCase();
  return t.includes("tenor") && t.includes("expiry");
}

/**
 * Classify an API column as shared, bid, or ask.
 * Side-specific titles look like "Bid Delta"; keys look like "bid_delta".
 */
export function classifyAdditionalDataColumn(col) {
  const key = columnKey(col);
  const title = columnTitle(col);
  const keyLower = key.toLowerCase();
  const titleLower = title.toLowerCase();

  if (keyLower.startsWith(`${BID_PREFIX}_`)) {
    return {
      side: BID_PREFIX,
      metricKey: key.slice(BID_PREFIX.length + 1),
      label: title.replace(/^Bid\s+/i, "") || key.slice(BID_PREFIX.length + 1),
    };
  }
  if (keyLower.startsWith(`${ASK_PREFIX}_`)) {
    return {
      side: ASK_PREFIX,
      metricKey: key.slice(ASK_PREFIX.length + 1),
      label: title.replace(/^Ask\s+/i, "") || key.slice(ASK_PREFIX.length + 1),
    };
  }
  if (titleLower.startsWith("bid ")) {
    return {
      side: BID_PREFIX,
      metricKey: title.slice(4).trim().toLowerCase().replace(/\s+/g, "_"),
      label: title.slice(4).trim(),
    };
  }
  if (titleLower.startsWith("ask ")) {
    return {
      side: ASK_PREFIX,
      metricKey: title.slice(4).trim().toLowerCase().replace(/\s+/g, "_"),
      label: title.slice(4).trim(),
    };
  }

  return {
    side: "shared",
    metricKey: `shared:${title || key || "col"}`,
    label: title || key,
  };
}

/**
 * Metric rows in display order: leading shared cols, then side greeks (once),
 * then trailing shared cols (# Months / # Days).
 */
export function buildAdditionalDataMetricRows(columns) {
  if (!Array.isArray(columns) || columns.length === 0) return [];

  const metrics = [];
  const sideMetricIndexByKey = new Map();

  columns.forEach((col, fieldIndex) => {
    const { side, metricKey, label } = classifyAdditionalDataColumn(col);

    if (side === "shared") {
      metrics.push({
        id: `${metricKey}:${fieldIndex}`,
        label,
        sharedIndex: fieldIndex,
        bidIndex: null,
        askIndex: null,
        isTenor: isTenorExpiryColumnTitle(label),
      });
      return;
    }

    const existingAt = sideMetricIndexByKey.get(metricKey);
    if (existingAt != null) {
      const existing = metrics[existingAt];
      if (side === BID_PREFIX) existing.bidIndex = fieldIndex;
      else existing.askIndex = fieldIndex;
      return;
    }

    sideMetricIndexByKey.set(metricKey, metrics.length);
    metrics.push({
      id: metricKey,
      label,
      sharedIndex: null,
      bidIndex: side === BID_PREFIX ? fieldIndex : null,
      askIndex: side === ASK_PREFIX ? fieldIndex : null,
      isTenor: false,
    });
  });

  return metrics;
}

/**
 * Ask-order entities: Summary, Leg 1, SLeg 1, … Bid columns reverse this list.
 */
export function buildAdditionalDataAskOrderEntities(summary, legs) {
  const entities = [];

  (Array.isArray(summary) ? summary : []).forEach((item, index) => {
    entities.push({
      id: `summary-${index}`,
      label: item?.label || "Summary",
      fields: item?.fields ?? [],
      isSummary: true,
    });
  });

  (Array.isArray(legs) ? legs : []).forEach((leg, legIndex) => {
    entities.push({
      id: `leg-${legIndex}`,
      label: leg?.label || `Leg ${legIndex + 1}`,
      fields: leg?.fields ?? [],
      isSummary: false,
    });

    (leg?.sublegs ?? []).forEach((subleg, sublegIndex) => {
      entities.push({
        id: `sleg-${legIndex}-${sublegIndex}`,
        label: subleg?.label || `SLeg ${sublegIndex + 1}`,
        fields: subleg?.fields ?? [],
        isSummary: false,
      });
    });
  });

  return entities;
}

export function getAdditionalDataFieldForMetric(fields, metric, side) {
  if (!Array.isArray(fields) || !metric) return null;

  let fieldIndex = null;
  if (metric.sharedIndex != null) {
    fieldIndex = metric.sharedIndex;
  } else if (side === BID_PREFIX) {
    fieldIndex = metric.bidIndex;
  } else if (side === ASK_PREFIX) {
    fieldIndex = metric.askIndex;
  }

  if (fieldIndex == null || fieldIndex < 0) return null;
  return fields[fieldIndex] ?? null;
}

/**
 * Full view model for the transposed top table.
 */
export function buildAdditionalDataTopTable(columns, summary, legs) {
  const metrics = buildAdditionalDataMetricRows(columns);
  const askEntities = buildAdditionalDataAskOrderEntities(summary, legs);
  const bidEntities = [...askEntities].reverse();

  const hasBid = metrics.some((m) => m.bidIndex != null);
  const hasAsk = metrics.some((m) => m.askIndex != null);
  // Shared-only payloads (no greek sides) still render entities under Bid.
  const showBid = hasBid || (!hasAsk && askEntities.length > 0);
  const showAsk = hasAsk;

  return {
    metrics,
    bidEntities: showBid ? bidEntities : [],
    askEntities: showAsk ? askEntities : [],
    showBid,
    showAsk,
  };
}

/**
 * Classify a bottom_values item as whole-option, bid-only, or ask-only.
 * Side-specific labels look like "Bid Total Premium".
 */
export function classifyAdditionalDataOptionItem(item) {
  const label = String(item?.label ?? "");
  const key = String(item?.key ?? "");
  const keyLower = key.toLowerCase();
  const labelLower = label.toLowerCase();

  if (keyLower.startsWith(`${BID_PREFIX}_`) || labelLower.startsWith("bid ")) {
    const baseLabel = label.replace(/^Bid\s+/i, "").trim() || label;
    const baseKey = keyLower.startsWith(`${BID_PREFIX}_`)
      ? key.slice(BID_PREFIX.length + 1)
      : baseLabel.toLowerCase().replace(/\s+/g, "_");
    return { side: BID_PREFIX, baseLabel, baseKey };
  }

  if (keyLower.startsWith(`${ASK_PREFIX}_`) || labelLower.startsWith("ask ")) {
    const baseLabel = label.replace(/^Ask\s+/i, "").trim() || label;
    const baseKey = keyLower.startsWith(`${ASK_PREFIX}_`)
      ? key.slice(ASK_PREFIX.length + 1)
      : baseLabel.toLowerCase().replace(/\s+/g, "_");
    return { side: ASK_PREFIX, baseLabel, baseKey };
  }

  return {
    side: "shared",
    baseLabel: label,
    baseKey: key || label.toLowerCase().replace(/\s+/g, "_") || "option",
  };
}

/** Editable Black-76 PDE grid keys from additional-info (display order). */
export const BLACK76_GRID_FIELD_KEYS = {
  FMAX: "black76_fmax",
  PRICE_STEPS: "black76_price_steps",
  TIME_STEPS: "black76_time_steps",
};

const BLACK76_GRID_FIELD_ORDER = [
  BLACK76_GRID_FIELD_KEYS.FMAX,
  BLACK76_GRID_FIELD_KEYS.PRICE_STEPS,
  BLACK76_GRID_FIELD_KEYS.TIME_STEPS,
];

export function isBlack76GridField(item) {
  const key = String(item?.key ?? "");
  return BLACK76_GRID_FIELD_ORDER.includes(key);
}

function getBlack76GridFieldRank(item) {
  const key = String(item?.key ?? "");
  const rank = BLACK76_GRID_FIELD_ORDER.indexOf(key);
  if (rank >= 0) return rank;

  const token = `${String(item?.label ?? "").toLowerCase()} ${key.toLowerCase()}`;
  if (/fmax/.test(token)) return 0;
  if (/price/.test(token) && /step/.test(token)) return 1;
  if (/time/.test(token) && /step/.test(token)) return 2;
  return null;
}

/**
 * Keep Fmax multiple above Price steps and Time steps in Additional Data.
 * Reorders only the matched indices so other bottom_values stay put.
 */
export function reorderPdeGridFieldsInBottomValues(bottomValues) {
  if (!Array.isArray(bottomValues) || bottomValues.length === 0) {
    return bottomValues;
  }

  const slots = [];
  bottomValues.forEach((item, index) => {
    const rank = getBlack76GridFieldRank(item);
    if (rank != null) slots.push({ index, rank, item });
  });

  if (slots.length < 2) return bottomValues;

  const sortedItems = [...slots].sort((a, b) => a.rank - b.rank).map((s) => s.item);
  const indices = slots.map((s) => s.index).sort((a, b) => a - b);
  const next = [...bottomValues];
  indices.forEach((idx, i) => {
    next[idx] = sortedItems[i];
  });
  return next;
}

/**
 * Turn bottom_values into table rows. Whole-option fields span Bid+Ask;
 * Bid/Ask pairs (e.g. Total Premium) share one row with per-side cells.
 */
export function buildAdditionalDataOptionRows(bottomValues) {
  if (!Array.isArray(bottomValues) || bottomValues.length === 0) return [];

  const orderedBottomValues = reorderPdeGridFieldsInBottomValues(bottomValues);

  const rows = [];
  const sideRowIndexByKey = new Map();

  orderedBottomValues.forEach((item, index) => {
    const { side, baseLabel, baseKey } = classifyAdditionalDataOptionItem(item);

    if (side === "shared") {
      rows.push({
        id: `option-shared-${baseKey || index}`,
        label: baseLabel,
        span: "all",
        shared: item,
        bid: null,
        ask: null,
      });
      return;
    }

    const existingAt = sideRowIndexByKey.get(baseKey);
    if (existingAt != null) {
      const existing = rows[existingAt];
      if (side === BID_PREFIX) existing.bid = item;
      else existing.ask = item;
      return;
    }

    sideRowIndexByKey.set(baseKey, rows.length);
    rows.push({
      id: `option-side-${baseKey || index}`,
      label: baseLabel,
      span: "sides",
      shared: null,
      bid: side === BID_PREFIX ? item : null,
      ask: side === ASK_PREFIX ? item : null,
    });
  });

  return rows;
}
